import { createEffect, createSignal, For, onCleanup, Show } from "solid-js";
import type { GameController } from "../../app/controller";
import type { ChessClock } from "../../app/clock";
import { PLAYER_ACCENT } from "../../app/config";
import { DEFAULT_MAX_PLIES, isInCheck } from "../../core";
import { muted, playCue, toggleMuted, unlockAudio } from "../../app/sound";
import { Board } from "../components/Board";
import { ActionPanel } from "../components/ActionPanel";
import { Hand } from "../components/Hand";
import { MoveLog } from "../components/MoveLog";
import { DeckTracker } from "../components/DeckTracker";
import { PlayerStatus } from "../components/Sidebar";
import { ThemeToggle } from "../components/ThemeToggle";
import { ClockReadout } from "../components/ChessClock";
import { TurnBanner } from "../components/TurnBanner";
import { RulesOverlay } from "../components/RulesOverlay";
import { Icon } from "../components/Icon";
import { HandoffScreen } from "./HandoffScreen";
import { GameOverScreen } from "./GameOverScreen";

export function GameScreen(props: {
  controller: GameController;
  onExit: () => void;
  /** Optional "back to home" action for the game-over modal, distinct from onExit. */
  onHome?: () => void;
  online?: boolean;
  /** Watching someone else's game: read-only, no hand, no resign. */
  spectator?: boolean;
  clock?: ChessClock;
  /** Present in single-player; true while the computer is deliberating. */
  aiThinking?: () => boolean;
  /** The viewer's rating change from a finished ranked game, if any. */
  ratingChange?: () => { readonly delta: number; readonly rating: number } | null;
}) {
  const c = props.controller;
  const state = () => c.store.state;
  const turnName = () => state().players[state().turn].name;
  const showHand = () => !(c.settings.hideHandsBetweenTurns && c.curtainUp());
  const [confirmResign, setConfirmResign] = createSignal(false);
  const [showRules, setShowRules] = createSignal(false);

  const vsAi = () => props.aiThinking !== undefined;
  // Both online and single-player lock the UI to one seat's perspective.
  const perspective = () => props.online || vsAi();

  // --- turn tracker ----------------------------------------------------------
  // A "turn" is a full move (both players act = 2 plies). The game is a draw at
  // the cap, so surface the count and warn as it approaches.
  const maxPlies = () => state().config.maxPlies ?? DEFAULT_MAX_PLIES;
  const maxTurns = () => Math.floor(maxPlies() / 2);
  const turnNo = () => Math.min(maxTurns(), Math.floor(state().ply / 2) + 1);
  const turnsLeft = () => maxTurns() - turnNo();
  const turnWarn = () => state().phase === "playing" && turnsLeft() <= 10 && turnsLeft() > 3;
  const turnDanger = () => state().phase === "playing" && turnsLeft() <= 3;

  // --- draw offers -----------------------------------------------------------
  // The seat "I" act as for draw purposes (my fixed seat online/vs-AI, or the
  // side to move in local hotseat).
  const mySide = () => c.mySeat ?? state().turn;
  const offer = () => c.drawOffer();
  const iOffered = () => offer() !== null && offer() === mySide();
  const incomingOffer = () =>
    state().phase === "playing" && offer() !== null && offer() !== mySide();
  const offererName = () => {
    const o = offer();
    return o === null ? "" : state().players[o].name;
  };
  const canOfferDraw = () => state().phase === "playing" && !props.spectator && offer() === null;

  // --- sound cues ------------------------------------------------------------
  // Drive synthesised SFX off observable state, which works identically for
  // local, AI, and online backends (lastEvents is local-only). We track the
  // newest log entry and the finished/check transitions, firing one cue each.
  let lastSounded = -1;
  let wasFinished = state().phase === "finished";
  createEffect(() => {
    const s = state();
    // Game-over flourish, once, on the transition into the finished phase.
    if (s.phase === "finished") {
      if (!wasFinished) {
        wasFinished = true;
        lastSounded = s.log.length;
        playCue("gameOver");
      }
      return;
    }
    wasFinished = false;
    const entry = s.log[s.log.length - 1];
    if (!entry || s.log.length === lastSounded) return;
    lastSounded = s.log.length;
    // Check takes precedence: an alert when the side to move is now in check.
    if (isInCheck(s, s.turn)) {
      playCue("check");
      return;
    }
    switch (entry.kind) {
      case "move":
      case "captainMove":
        playCue(entry.captured ? "capture" : "move");
        break;
      case "deploy":
        playCue("deploy");
        break;
      case "card":
        playCue(entry.revived ? "deploy" : "move");
        break;
      default:
        break; // draw / discard / pass: silent
    }
  });

  // Online: nudge the browser tab when it becomes your move so a player who
  // tabbed away notices. Title-only (no permission prompt); restored on cleanup.
  if (props.online && !props.spectator) {
    const base = document.title;
    createEffect(() => {
      const yourMove = state().phase === "playing" && c.canAct();
      document.title = yourMove ? "● Your move — Chessdeck" : base;
    });
    onCleanup(() => {
      document.title = base;
    });
  }

  // Clicking the brand returns home. Mid-game it confirms first, since leaving a
  // live game forfeits it (online play can be rejoined from the room link).
  const [confirmLeave, setConfirmLeave] = createSignal(false);
  const goHome = () => (props.onHome ?? props.onExit)();
  const clickBrand = () => {
    if (state().phase === "playing" && !props.spectator) setConfirmLeave(true);
    else goHome();
  };

  const turnLabel = () => {
    if (props.spectator) return `${turnName()} to move`;
    if (perspective()) {
      if (c.canAct()) return "Your move";
      if (props.aiThinking?.()) return "Computer is thinking…";
      return `${turnName()} to move`;
    }
    return `${turnName()} to act`;
  };

  return (
    <div class="app app--wide" onPointerDown={unlockAudio} onKeyDown={unlockAudio}>
      <div class="topbar">
        <div class="brand">
          <button class="brand-home" title="Back to home" onClick={clickBrand}>
            <h1>Chessdeck</h1>
          </button>
          <Show when={state().phase === "playing"}>
            <span class="tag" style={{ color: PLAYER_ACCENT[state().turn] }}>
              {turnLabel()}
            </span>
            <span
              class="turnmeter"
              classList={{ warn: turnWarn(), danger: turnDanger() }}
              title={`The game is a draw at turn ${maxTurns()}.${
                turnsLeft() <= 10 ? ` ${turnsLeft()} turn${turnsLeft() === 1 ? "" : "s"} left.` : ""
              }`}
            >
              Turn {turnNo()} / {maxTurns()}
            </span>
          </Show>
        </div>
        <Show when={props.clock}>
          {(clock) => (
            <div class="topclocks">
              <For each={[0, 1] as const}>
                {(seat) => {
                  const live = () => state().turn === seat && state().phase === "playing";
                  return (
                    <div
                      class="topclock"
                      classList={{ active: live() }}
                      style={live() ? { background: PLAYER_ACCENT[seat] } : undefined}
                    >
                      <span class="swatch" style={{ background: PLAYER_ACCENT[seat] }} />
                      <span class="tc-name">{state().players[seat].name}</span>
                      <ClockReadout clock={clock()} owner={seat} />
                    </div>
                  );
                }}
              </For>
            </div>
          )}
        </Show>
        <div class="topbar-actions">
          <button class="icon-link" title="How to play" onClick={() => setShowRules(true)}>
            <Icon name="help" />
          </button>
          <button
            class="icon-link"
            title={muted() ? "Unmute sound" : "Mute sound"}
            aria-label={muted() ? "Unmute sound" : "Mute sound"}
            aria-pressed={muted()}
            onClick={() => {
              unlockAudio();
              toggleMuted();
            }}
          >
            <Icon name={muted() ? "volume-x" : "volume"} />
          </button>
          <ThemeToggle />
          <Show when={!props.online && !vsAi()}>
            <label class="check">
              <input
                type="checkbox"
                checked={c.settings.hideHandsBetweenTurns}
                onChange={(e) => c.setHideHands(e.currentTarget.checked)}
              />
              Hide hands
            </label>
          </Show>
          <Show when={!props.online}>
            <button class="btn" onClick={() => c.undo()} disabled={!c.store.canUndo}>
              Undo
            </button>
          </Show>
          <Show when={state().phase === "playing" && !props.spectator}>
            <button
              class="btn"
              disabled={!canOfferDraw()}
              title={iOffered() ? "Waiting for your opponent to respond" : "Offer a draw"}
              onClick={() => c.offerDraw()}
            >
              {iOffered() ? "Draw offered…" : "Offer draw"}
            </button>
            <button class="btn btn-danger" onClick={() => setConfirmResign(true)}>
              Resign
            </button>
          </Show>
          <button class="btn btn-ghost" onClick={props.onExit}>
            {props.online ? "Leave" : "New match"}
          </button>
        </div>
      </div>

      <div class="game">
        <div class="col">
          <PlayerStatus controller={c} owner={1} clock={props.clock} />
          <PlayerStatus controller={c} owner={0} clock={props.clock} />
        </div>

        <div class="board-col">
          <Show when={incomingOffer()}>
            <div class="draw-offer-banner">
              <span>
                {perspective() ? "Your opponent" : offererName()} offers a draw.
              </span>
              <div class="draw-offer-actions">
                <button class="btn btn-primary" onClick={() => c.acceptDraw()}>
                  Accept draw
                </button>
                <button class="btn btn-ghost" onClick={() => c.declineDraw()}>
                  Decline
                </button>
              </div>
            </div>
          </Show>
          <TurnBanner
            controller={c}
            perspective={perspective()}
            spectator={props.spectator}
            online={props.online}
            aiThinking={props.aiThinking}
          />
          <Board controller={c} />
        </div>

        <div class="col">
          <Show when={!props.spectator}>
            <div class="panel">
              <h2>{perspective() ? "Your hand" : `${state().players[c.viewer()].name}'s hand`}</h2>
              <Show when={showHand()} fallback={<div class="hand-empty">Hidden until reveal.</div>}>
                <Hand controller={c} />
              </Show>
            </div>
            <div class="panel">
              <ActionPanel controller={c} online={perspective()} />
            </div>
          </Show>
          <div class="panel">
            <h2>Decks &amp; losses</h2>
            <DeckTracker controller={c} />
          </div>
          <div class="panel">
            <h2>Move log</h2>
            <MoveLog controller={c} />
          </div>
        </div>
      </div>

      <Show when={c.curtainUp() && state().phase === "playing"}>
        <HandoffScreen controller={c} />
      </Show>

      <Show when={confirmResign() && state().phase === "playing"}>
        <div class="overlay">
          <div class="overlay-card">
            <div class="big-glyph"><Icon name="flag" /></div>
            <h2>Resign?</h2>
            <p>
              {perspective()
                ? "You concede the game — your opponent wins."
                : `${turnName()} concedes — ${state().players[state().turn === 0 ? 1 : 0].name} wins.`}
            </p>
            <div class="field-row" style={{ "justify-content": "center", gap: "8px" }}>
              <button class="btn btn-ghost" onClick={() => setConfirmResign(false)}>
                Cancel
              </button>
              <button
                class="btn btn-danger"
                onClick={() => {
                  c.resign();
                  setConfirmResign(false);
                }}
              >
                Resign
              </button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={confirmLeave()}>
        <div class="overlay">
          <div class="overlay-card">
            <div class="big-glyph"><Icon name="logout" /></div>
            <h2>Leave the game?</h2>
            <p>
              {props.online
                ? "You'll return to the home screen. You can rejoin this game from its link."
                : "You'll return to the home screen. This game won't be saved."}
            </p>
            <div class="field-row" style={{ "justify-content": "center", gap: "8px" }}>
              <button class="btn btn-ghost" onClick={() => setConfirmLeave(false)}>
                Keep playing
              </button>
              <button
                class="btn btn-primary"
                onClick={() => {
                  setConfirmLeave(false);
                  goHome();
                }}
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      </Show>

      <GameOverScreen
        controller={c}
        onExit={props.onExit}
        onHome={props.onHome}
        online={props.online}
        vsAi={vsAi()}
        ratingChange={props.ratingChange}
      />

      <Show when={showRules()}>
        <RulesOverlay onClose={() => setShowRules(false)} />
      </Show>
    </div>
  );
}
